import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import XLSX from 'xlsx';
import { selectedJobAtom } from '../store/company';
import { loadJobPurchaseOrdersLive, loadPOMatchStatus } from '../db/company';
import {
    PO_TYPES, RECEIPT_STATES, typeLabel, buildPurchaseOrders, amounts, pctReceived, totalsOf,
    filterPurchaseOrders, sortPurchaseOrders, countBy, exportRows,
} from './purchaseOrdersData';

// Purchase Orders tab: every PO on the selected job, and what has been received
// against each line. The web form of the "PO Overview" page in the SC-PO Spend
// Power BI report. Both reads are live (JDE through the bi_dw mirror on
// muleprod), not the version snapshot the forecast tabs use: receiving keeps
// moving after a WIP is cut. The pure part is purchaseOrdersData.js.

// A job's POs take ~10 s to read, so a job seen once this session reopens from
// here; Refresh reads it again.
const cache = new Map();

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const qtyFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const money = (n) => usd.format(n || 0);
const day = (d) => (d ? moment(d, 'YYYY-MM-DD').format('M/D/YYYY') : '');
const pctText = (p) => `${Math.round(p * 100)}%`;

// status colours (STRIKE_STYLE section 7): the word always prints, the dot repeats it
const STATE_TONE = {
    'Received': 'var(--success)',
    'Partly received': 'var(--primary)',
    'Not received': 'var(--text-secondary)',
    'Closed': 'var(--text-secondary)',
    'Vouchered': 'var(--success)',
    'Partly vouchered': 'var(--primary)',
    'Not vouchered': 'var(--text-secondary)',
    'Reversed': 'var(--text-secondary)',
};
const STATE_TEXT = { 'Received': 'var(--success-text)', 'Vouchered': 'var(--success-text)' };

const StatusDot = ({ word }) => (word ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', color: STATE_TEXT[word] || 'var(--foreground)' }}>
        <span style={{ width: 7, height: 7, borderRadius: 'var(--radius-pill)', background: STATE_TONE[word] || 'var(--text-secondary)', flexShrink: 0 }} />
        {word}
    </span>
) : null);

const ReceivedBar = ({ p }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', width: '100%' }}>
        <span style={{ width: 40, height: 6, borderRadius: 'var(--radius-pill)', background: 'var(--muted)', overflow: 'hidden', flexShrink: 0 }}>
            <span style={{ display: 'block', height: '100%', width: `${Math.max(0, Math.min(1, p)) * 100}%`, background: 'var(--chart-1)' }} />
        </span>
        <span style={{ minWidth: 34, textAlign: 'right' }}>{pctText(p)}</span>
    </span>
);

// The page gutter (24 px, 16 on a phone) and the row hover can't be written as
// inline styles.
const PAGE_CSS = `
.po-tab { padding: 24px; box-sizing: border-box; }
@media (max-width: 640px) { .po-tab { padding: 16px; } }
.po-tab tr[data-po-row]:hover { background: color-mix(in srgb, var(--muted) 60%, transparent); }
.po-tab button[aria-pressed="false"]:hover { color: var(--foreground); }
.po-tab button[data-quiet]:not(:disabled):hover { background: var(--muted) !important; }
`;

const card = {
    background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
    boxShadow: 'var(--shadow-xs)',
};

const Stat = ({ label, value, note }) => (
    <div style={{ ...card, padding: '14px 16px', minWidth: 170, flex: '1 1 170px' }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)' }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--foreground)', marginTop: 2 }}>{value}</div>
        {note ? <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{note}</div> : null}
    </div>
);

// A row of buttons that switch one value: the picked one is navy (section 6, .segmented).
const Segmented = ({ label, options, value, onChange }) => (
    <div role="group" aria-label={label} style={{ display: 'inline-flex', gap: 2, padding: 3, background: 'var(--muted)', borderRadius: 'var(--radius)', flexWrap: 'wrap' }}>
        {options.map((o) => {
            const on = o.value === value;
            return (
                <button
                    key={o.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onChange(o.value)}
                    style={{
                        height: 26, padding: '0 10px', border: 'none', borderRadius: 'var(--radius-inner)', cursor: 'pointer',
                        fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', fontFamily: 'inherit',
                        background: on ? 'var(--primary)' : 'transparent',
                        color: on ? 'var(--primary-foreground)' : 'var(--text-secondary)',
                    }}
                >
                    {o.label}
                    {o.count != null ? <span style={{ marginLeft: 6, opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}>{o.count}</span> : null}
                </button>
            );
        })}
    </div>
);

const OutlineButton = ({ label, onClick, disabled }) => (
    <button
        type="button"
        data-quiet="true"
        onClick={onClick}
        disabled={disabled}
        style={{
            height: 32, padding: '0 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
            background: 'var(--background)', color: 'var(--foreground)', boxShadow: 'var(--shadow-xs)',
            fontSize: 14, fontWeight: 500, fontFamily: 'inherit', whiteSpace: 'nowrap',
            cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
        }}
    >
        {label}
    </button>
);

const th = {
    fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', textAlign: 'left', background: 'var(--subtle)',
    borderBottom: '1px solid var(--border)', padding: '8px 10px', whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 1,
};
const td = { padding: '8px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', fontSize: 14 };
const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const caption = { fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 };

// Widths come from the content; the vendor column takes what is left and wraps.
const PO_COLS = [
    { key: 'po', label: 'PO' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'orderDate', label: 'Order date' },
    { key: 'ordered', label: 'Ordered', num: true },
    { key: 'received', label: 'Received', num: true },
    { key: 'open', label: 'Open', num: true },
    { key: 'pct', label: 'Received %', num: true },
    { key: 'lastReceived', label: 'Last received' },
    { key: 'state', label: 'Status' },
];

const LineTable = ({ po, withTax }) => (
    <table style={{ borderCollapse: 'collapse', width: '100%', background: 'var(--background)' }}>
        <thead>
            <tr>
                {['Line', 'Description', 'Cost type · phase', 'Qty received', 'Ordered', 'Received', 'Open', 'Voucher', 'Status'].map((h, i) => (
                    <th key={h} style={{ ...th, position: 'static', background: 'var(--background)', ...(i >= 4 && i <= 6 ? { textAlign: 'right' } : null) }}>{h}</th>
                ))}
            </tr>
        </thead>
        <tbody>
            {po.lines.map((l) => {
                const a = amounts(l, withTax);
                const hasQty = Math.abs(l.qtyOrdered) > 0.0001;
                return (
                    <tr key={l.key}>
                        <td style={{ ...td, ...num, textAlign: 'left', width: 48 }}>{l.lineNumber}</td>
                        <td style={{ ...td, minWidth: 180 }}>
                            <div>{l.description || '—'}</div>
                            {l.description2 ? <div style={caption}>{l.description2}</div> : null}
                            {po.type === 'OL' && (l.rentalStart || l.rentalEnd)
                                ? <div style={caption}>Rental {day(l.rentalStart)} – {day(l.rentalEnd)}</div>
                                : (l.promisedDate && l.state !== 'Received' ? <div style={caption}>Promised {day(l.promisedDate)}</div> : null)}
                        </td>
                        <td style={{ ...td, minWidth: 120 }}>
                            <div>{l.costType} {l.costTypeDesc}</div>
                            <div style={caption}>{l.phase} {l.phaseDesc}</div>
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {hasQty
                                ? <div>{qtyFmt.format(l.qtyReceived)} of {qtyFmt.format(l.qtyOrdered)} {l.uom}</div>
                                : <div>{l.uom || '—'}</div>}
                            {hasQty && l.unitPrice ? <div style={caption}>{money(l.unitPrice)} / {l.uom}</div> : null}
                        </td>
                        <td style={{ ...td, ...num }}>{money(a.ordered)}</td>
                        <td style={{ ...td, ...num }}>
                            <div>{money(a.received)}</div>
                            {l.lastReceived ? <div style={caption}>Last {day(l.lastReceived)}</div> : null}
                        </td>
                        <td style={{ ...td, ...num }}>{money(a.open)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            <StatusDot word={l.voucher} />
                            {l.invoiceNumber ? (
                                <div style={caption}>
                                    {l.tapUrl
                                        ? <a href={l.tapUrl} target="_blank" rel="noopener noreferrer">Invoice {l.invoiceNumber}</a>
                                        : `Invoice ${l.invoiceNumber}`}
                                </div>
                            ) : null}
                            {l.voucher === 'Partly vouchered' && l.notVouchered > 0.005 ? <div style={caption}>{money(l.notVouchered)} not vouchered</div> : null}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }} title={`JDE next status ${l.statusNext} ${l.statusNextDesc}`.trim()}>
                            <StatusDot word={l.state} />
                        </td>
                    </tr>
                );
            })}
        </tbody>
    </table>
);

const PurchaseOrders = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const job = selectedJob ? String(selectedJob).trim() : '';
    const [data, setData] = useState(() => (job && cache.get(job)) || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [type, setType] = useState('All');
    const [state, setState] = useState('All');
    const [withTax, setWithTax] = useState(true);
    const [sort, setSort] = useState({ col: 'orderDate', dir: 'desc' });
    const [expanded, setExpanded] = useState(() => new Set());
    const requestId = useRef(0);

    const load = useCallback(async (force) => {
        if (!job) return;
        if (!force && cache.get(job)) {
            setData(cache.get(job));
            setError('');
            return;
        }
        const id = ++requestId.current;
        setLoading(true);
        setError('');
        try {
            const [lines, match] = await Promise.all([
                loadJobPurchaseOrdersLive({ id: job }),
                loadPOMatchStatus({ id: job }).catch(() => null),
            ]);
            if (id !== requestId.current) return;
            if (!lines || lines.error || !Array.isArray(lines.data)) {
                setError('The PO lines did not load. Try Refresh.');
                setData(null);
                return;
            }
            const matchRows = Array.isArray(match?.data) ? match.data : null;
            const next = { job, lines: lines.data, match: matchRows || [], matchMissing: !matchRows, loadedAt: new Date() };
            cache.set(job, next);
            setData(next);
        } catch (e) {
            if (id === requestId.current) {
                setError('The PO lines did not load. Try Refresh.');
                setData(null);
            }
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [job]);

    useEffect(() => {
        setData((job && cache.get(job)) || null);
        setExpanded(new Set());
        load(false);
    }, [job, load]);

    const pos = useMemo(() => (data && data.job === job ? buildPurchaseOrders(data.lines, data.match) : []), [data, job]);
    const shown = useMemo(
        () => sortPurchaseOrders(filterPurchaseOrders(pos, { search, type, state }), sort.col, sort.dir, withTax),
        [pos, search, type, state, sort, withTax],
    );
    const totals = useMemo(() => totalsOf(shown, withTax), [shown, withTax]);
    const typeCounts = useMemo(() => countBy(filterPurchaseOrders(pos, { search, state }), 'type'), [pos, search, state]);
    const stateCounts = useMemo(() => countBy(filterPurchaseOrders(pos, { search, type }), 'state'), [pos, search, type]);
    const latestChange = useMemo(() => (data?.lines || []).reduce((m, r) => (r.Last_Modified_DateTime && r.Last_Modified_DateTime > m ? r.Last_Modified_DateTime : m), ''), [data]);

    const toggle = (key) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const allOpen = shown.length > 0 && shown.every((po) => expanded.has(po.key));
    const setSortCol = (col) => setSort((s) => (s.col === col
        ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: ['po', 'vendor', 'state'].includes(col) ? 'asc' : 'desc' }));

    const exportXlsx = () => {
        const ws = XLSX.utils.json_to_sheet(exportRows(shown, withTax));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'PO lines');
        XLSX.writeFile(wb, `Purchase_Orders_${job}_${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    if (!job) {
        return (
            <div style={{ padding: 24 }}>
                <div style={{ ...card, padding: 20, color: 'var(--text-secondary)' }}>Pick a job in the top bar to see its purchase orders.</div>
            </div>
        );
    }

    // Before the first read lands there is no number to show, not a zero.
    const stat = (n) => (data ? usd0.format(n) : '—');
    const typeOptions = [{ value: 'All', label: 'All types' }].concat(
        Object.keys(PO_TYPES).filter((t) => typeCounts[t] || type === t).map((t) => ({ value: t, label: typeLabel(t), count: typeCounts[t] || 0 })),
    );
    const stateOptions = [{ value: 'All', label: 'Any status' }].concat(
        RECEIPT_STATES.map((s) => ({ value: s, label: s, count: stateCounts[s] || 0 })),
    );

    return (
        <div className="po-tab">
            <style>{PAGE_CSS}</style>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={{ flex: '1 1 320px' }}>
                    <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--foreground)' }}>Purchase orders</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                        Job {job} · live from JDE
                        {data?.loadedAt ? ` · loaded ${moment(data.loadedAt).format('h:mm A')}` : ''}
                        {latestChange ? ` · latest PO change ${moment.utc(latestChange).format('M/D/YYYY')}` : ''}
                    </div>
                </div>
                <OutlineButton label={allOpen ? 'Collapse all' : 'Expand all'} onClick={() => setExpanded(allOpen ? new Set() : new Set(shown.map((po) => po.key)))} disabled={!shown.length} />
                <OutlineButton label="Export to Excel" onClick={exportXlsx} disabled={!shown.length} />
                <OutlineButton label={loading ? 'Loading…' : 'Refresh'} onClick={() => load(true)} disabled={loading} />
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <Stat label={withTax ? 'Ordered, with tax' : 'Ordered'} value={stat(totals.ordered)} note={data ? `${totals.pos} POs · ${totals.lines} lines` : null} />
                <Stat label={withTax ? 'Received, with tax' : 'Received'} value={stat(totals.received)} note={data && totals.ordered ? `${pctText(totals.received / totals.ordered)} of ordered` : null} />
                <Stat label={withTax ? 'Open, with tax' : 'Open'} value={stat(totals.open)} note="Still to receive" />
                <Stat label="Received, not vouchered" value={data?.matchMissing ? '—' : stat(totals.notVouchered)} note={data?.matchMissing ? 'Voucher status did not load' : 'No vendor invoice matched yet'} />
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search PO, vendor, item, invoice"
                    aria-label="Search purchase orders"
                    style={{
                        height: 32, width: 260, padding: '0 10px', borderRadius: 'var(--radius)', border: '1px solid var(--input)',
                        background: 'var(--background)', color: 'var(--foreground)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
                    }}
                />
                <Segmented label="PO type" options={typeOptions} value={type} onChange={setType} />
                <Segmented label="Receipt status" options={stateOptions} value={state} onChange={setState} />
                <Segmented
                    label="Sales tax"
                    options={[{ value: true, label: 'With tax' }, { value: false, label: 'Before tax' }]}
                    value={withTax}
                    onChange={setWithTax}
                />
            </div>

            {error ? (
                <div role="alert" style={{ background: 'var(--destructive-tint)', color: 'var(--destructive-text)', border: '1px solid var(--destructive-edge)', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 12, fontSize: 14 }}>
                    {error}
                </div>
            ) : null}
            {data?.matchMissing && !loading ? (
                <div style={{ background: 'var(--warning-tint)', color: 'var(--warning-text)', border: '1px solid var(--warning-edge)', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 12, fontSize: 14 }}>
                    Receipt dates and voucher status did not load; quantities and amounts are complete. Refresh to try again.
                </div>
            ) : null}

            {/* The table fills the column and wraps long vendor names; only when
                it can't fit at all does the page scroll sideways, rather than the
                table being cut off inside its card. */}
            <div style={{ ...card, width: 'fit-content', minWidth: '100%' }}>
                <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={{ ...th, width: 36, borderTopLeftRadius: 'var(--radius-card)' }} aria-label="Expand" />
                            {PO_COLS.map((c, i) => {
                                const on = sort.col === c.key;
                                return (
                                    <th
                                        key={c.key}
                                        aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                        style={{ ...th, textAlign: c.num ? 'right' : 'left', ...(i === PO_COLS.length - 1 ? { borderTopRightRadius: 'var(--radius-card)' } : null) }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setSortCol(c.key)}
                                            style={{
                                                border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', font: 'inherit',
                                                color: on ? 'var(--foreground)' : 'inherit', fontWeight: 500, borderRadius: 'var(--radius-inner)',
                                            }}
                                        >
                                            {c.label}{on ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                                        </button>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !pos.length ? (
                            <tr>
                                <td colSpan={PO_COLS.length + 1} style={{ ...td, color: 'var(--text-secondary)', padding: '20px 16px' }}>
                                    Reading this job&rsquo;s purchase orders from JDE. This takes about ten seconds.
                                </td>
                            </tr>
                        ) : null}
                        {!loading && data && !pos.length ? (
                            <tr>
                                <td colSpan={PO_COLS.length + 1} style={{ ...td, color: 'var(--text-secondary)', padding: '20px 16px' }}>
                                    No purchase orders on job {job}.
                                </td>
                            </tr>
                        ) : null}
                        {pos.length > 0 && !shown.length ? (
                            <tr>
                                <td colSpan={PO_COLS.length + 1} style={{ ...td, color: 'var(--text-secondary)', padding: '20px 16px' }}>
                                    No purchase orders match these filters.
                                </td>
                            </tr>
                        ) : null}
                        {shown.map((po) => {
                            const open = expanded.has(po.key);
                            const a = amounts(po, withTax);
                            return (
                                <React.Fragment key={po.key}>
                                    <tr
                                        onClick={() => toggle(po.key)}
                                        style={{ cursor: 'pointer', background: open ? 'var(--muted)' : undefined }}
                                        data-po-row="true"
                                    >
                                        <td style={{ ...td, padding: '8px 0 8px 10px' }}>
                                            <button
                                                type="button"
                                                data-quiet="true"
                                                aria-expanded={open}
                                                aria-label={`${open ? 'Hide' : 'Show'} lines of PO ${po.poNumber}`}
                                                onClick={(e) => { e.stopPropagation(); toggle(po.key); }}
                                                style={{
                                                    width: 24, height: 24, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0,
                                                    color: 'var(--text-secondary)', borderRadius: 'var(--radius-inner)', fontSize: 12, fontFamily: 'inherit',
                                                }}
                                            >
                                                {open ? '▾' : '▸'}
                                            </button>
                                        </td>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                            <div style={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{po.poNumber}</div>
                                            <div style={caption} title={`${po.type} ${po.typeDesc}`}>{typeLabel(po.type)} · {po.lines.length} {po.lines.length === 1 ? 'line' : 'lines'}</div>
                                        </td>
                                        <td style={{ ...td, minWidth: 160 }}>
                                            <div>{po.vendor || '—'}</div>
                                            <div style={caption}>{po.vendorNumber}</div>
                                        </td>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(po.orderDate)}</td>
                                        <td style={{ ...td, ...num }}>{money(a.ordered)}</td>
                                        <td style={{ ...td, ...num }}>{money(a.received)}</td>
                                        <td style={{ ...td, ...num }}>{money(a.open)}</td>
                                        <td style={{ ...td, ...num }}><ReceivedBar p={pctReceived(po)} /></td>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(po.lastReceived) || '—'}</td>
                                        <td style={{ ...td }}><StatusDot word={po.state} /></td>
                                    </tr>
                                    {open ? (
                                        <tr>
                                            <td style={{ ...td, background: 'var(--subtle)' }} />
                                            <td colSpan={PO_COLS.length} style={{ ...td, padding: '8px 12px 12px 0', background: 'var(--subtle)' }}>
                                                <div style={{ ...card, borderRadius: 'var(--radius)', boxShadow: 'none' }}>
                                                    <LineTable po={po} withTax={withTax} />
                                                </div>
                                            </td>
                                        </tr>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                    {shown.length ? (
                        <tfoot>
                            <tr>
                                <td style={{ ...td, borderBottom: 'none' }} />
                                <td colSpan={3} style={{ ...td, borderBottom: 'none', fontWeight: 600 }}>
                                    Total · {totals.pos} {totals.pos === 1 ? 'PO' : 'POs'}
                                </td>
                                <td style={{ ...td, ...num, borderBottom: 'none', fontWeight: 600 }}>{money(totals.ordered)}</td>
                                <td style={{ ...td, ...num, borderBottom: 'none', fontWeight: 600 }}>{money(totals.received)}</td>
                                <td style={{ ...td, ...num, borderBottom: 'none', fontWeight: 600 }}>{money(totals.open)}</td>
                                <td style={{ ...td, ...num, borderBottom: 'none', fontWeight: 600 }}>{totals.ordered ? pctText(totals.received / totals.ordered) : ''}</td>
                                <td colSpan={2} style={{ ...td, borderBottom: 'none' }} />
                            </tr>
                        </tfoot>
                    ) : null}
                </table>
            </div>
        </div>
    );
};

export default PurchaseOrders;
